/**
 * The maths behind the photo viewer's pinch-zoom, kept away from the DOM.
 *
 * Everything here describes one transform: `translate(x, y) scale(scale)` with
 * the origin at the element's centre, which is where a `position: fixed`,
 * flex-centred image already sits. Coordinates are viewport pixels — the same
 * ones a PointerEvent reports — so the caller never has to convert anything.
 */

export interface Zoom {
  scale: number;
  /** Translate applied *after* the scale, in viewport pixels. */
  x: number;
  y: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Box {
  width: number;
  height: number;
}

/** The photo as it opens: whole, centred, untouched. */
export const FIT: Zoom = { scale: 1, x: 0, y: 0 };

/**
 * Uploads are re-encoded to at most 1600px on the long edge (server/src/images.ts),
 * so on a 3x phone screen the pixels run out somewhere around 3x. Four leaves a
 * little room to look closer at something without the picture turning to mush.
 */
export const MAX_SCALE = 4;

/** Where a double tap lands — far enough in to be worth the tap. */
export const DOUBLE_TAP_SCALE = 2.5;

/** A second tap is only the same gesture if it is soon and near. */
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP = 24;

export interface Tap extends Point {
  at: number;
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Rescales while holding `anchor` — the point between the fingers, or under the
 * cursor — still on screen.
 *
 * A point sits at `centre + local * scale + translate`. Solving that for the
 * translate that keeps the same `local` under the same `anchor` after the scale
 * changes gives `T' = d - (d - T) * (next / scale)`, where `d` is the anchor
 * measured from the centre. Which is to say: the picture grows away from the
 * fingers rather than away from the middle of the screen.
 */
export function zoomTo(current: Zoom, scale: number, anchor: Point, centre: Point): Zoom {
  const next = Math.min(MAX_SCALE, Math.max(1, scale));
  const ratio = next / current.scale;
  const dx = anchor.x - centre.x;
  const dy = anchor.y - centre.y;
  return {
    scale: next,
    x: dx - (dx - current.x) * ratio,
    y: dy - (dy - current.y) * ratio,
  };
}

/**
 * Stops a pan before the picture's edge comes inside the screen.
 *
 * `image` is the *layout* size of the img — what it measures at scale 1, which
 * is what `offsetWidth`/`offsetHeight` report whatever transform is on it. An
 * axis whose scaled size still fits inside the container has no slack at all
 * and is pinned to 0, so the photo re-centres itself on the way back out.
 */
export function clampPan(zoom: Zoom, image: Box, container: Box): Zoom {
  if (zoom.scale <= 1) return FIT;
  const slackX = Math.max(0, (image.width * zoom.scale - container.width) / 2);
  const slackY = Math.max(0, (image.height * zoom.scale - container.height) / 2);
  return {
    scale: zoom.scale,
    x: Math.min(slackX, Math.max(-slackX, zoom.x)),
    y: Math.min(slackY, Math.max(-slackY, zoom.y)),
  };
}

export function isDoubleTap(previous: Tap | null, next: Tap): boolean {
  if (!previous) return false;
  return next.at - previous.at <= DOUBLE_TAP_MS && distance(previous, next) <= DOUBLE_TAP_SLOP;
}

/** Double tap is a switch, not a step: in at the tap, or all the way back out. */
export function toggleZoom(current: Zoom, anchor: Point, centre: Point): Zoom {
  if (current.scale > 1) return FIT;
  return zoomTo(current, DOUBLE_TAP_SCALE, anchor, centre);
}
